import '../index.scss';

import clsx from 'clsx';
import { forwardRef, PropsWithChildren, useState } from 'react';

import { RibbonButton, RibbonButtonProps } from './RibbonButton';
import { RibbonButtonSliderContext, RibbonSliderAlignment, ribbonSliderAlignments } from './RibbonButtonSliderContext';
import { v4 } from '../common/uuid';

export interface RibbonSliderProps extends RibbonButtonProps {
  autoClose?: boolean;
  alignment: RibbonSliderAlignment;
  sliderClassName?: string;
}

export const RibbonButtonSlider = forwardRef<HTMLButtonElement, PropsWithChildren<RibbonSliderProps>>(
  function RibbonButtonSlider(
    { alignment, sliderClassName, autoClose = false, children, onClick, ...buttonProps },
    ref,
  ) {
    const [uniqueId] = useState(v4());
    const [menuUniqueId] = useState(`toolbarSliderMenu-${v4()}`);
    const [positionAnchor] = useState(`--toolbarSliderAnchor-${uniqueId}`);

    const defaultOnClick = () => setTimeout(focusNextRibbonButton, 0);

    const alignments = ribbonSliderAlignments[alignment];

    return (
      <RibbonButtonSliderContext.Provider value={{ autoClose, positionAnchor, menuUniqueId }}>
        <RibbonButton
          ref={ref}
          popoverTarget={menuUniqueId}
          popoverTargetAction={'toggle'}
          anchorName={positionAnchor}
          {...buttonProps}
          onClick={onClick ?? defaultOnClick}
        />
        <nav
          id={menuUniqueId}
          {...{ ['popover' as never]: 'auto' }}
          role="menu"
          aria-label="Menu"
          className={clsx(`RibbonSliderMenu RibbonSliderMenu-${alignment}`, sliderClassName)}
          style={{
            positionAnchor,
            [alignments[0]]: `anchor(${positionAnchor} ${alignments[1]})`,
            [alignments[2]]: `anchor(${positionAnchor} ${alignments[2]})`,
            ...(alignments[3] && { transform: `translate(${alignments[3]})` }),
          }}
        >
          {children}
        </nav>
      </RibbonButtonSliderContext.Provider>
    );
  },
);

const focusNextRibbonButton = () => {
  let button =
    document.activeElement?.nextElementSibling?.querySelectorAll<HTMLButtonElement>('button.RibbonButton-selected')[0];
  if (!button) {
    button = document.activeElement?.nextElementSibling?.querySelectorAll('button')[0];
  }
  button?.focus();
};
